<template>
  <div
    class="sp-radio-group"
    :class="classModifiers"
    part="base"
    role="radiogroup"
    :aria-label="attrs.ariaLabel"
    :aria-labelledby="ariaLabelledby"
    :aria-invalid="ariaInvalid"
    :aria-disabled="ariaDisabled"
  >
    <sp-label v-if="label" :id="labelId" class="sp-radio-group__label" part="label" block>
      {{ label }}
    </sp-label>

    <div ref="itemsContainer" class="sp-radio-group__items" part="items">
      <slot />
    </div>

    <div v-if="showError" class="sp-radio-group__error" part="error">{{ errorText }}</div>
  </div>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, ref, toRef, useAttrs, useTemplateRef, watch } from "vue";

import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { t } from "#ce/shared/forms-i18n";
import { formAssociatedHost } from "#ce/framework/utils";
import { getSlotContentByTagName } from "#ce/utils/slots";
import { isNoSelection, isPresent, toArray, toBoolean } from "#ce/utils/props";
import { props as radioGroupProps } from "./props.js";

// sp-radio-group submits the selected value under `name`, so it is a genuine
// form-associated control (opt-in required since form association is opt-in).
defineOptions(formAssociatedHost());

defineEmits(["input"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(radioGroupProps);

const itemsContainer = useTemplateRef("itemsContainer");

// The `value` attribute seeds the selection and names what a form reset restores; the `value`
// property is the live selection. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();
const defaultValue = ref(selectionOrNull(attrs.value));
const selectedValue = ref(defaultValue.value);

// A selection of "" would satisfy `required` while no radio is picked, so every channel that
// names a selection — the seed, a later attribute, a property write, the reset default — resolves
// "no selection" the same way.
function selectionOrNull(value) {
  return isNoSelection(value) ? null : value;
}

watch(
  () => attrs.value,
  (value) => {
    defaultValue.value = selectionOrNull(value);
    selectedValue.value = defaultValue.value;
  },
);

const { exposeProperties } = useExpose();

exposeProperties({
  type: {
    configurable: true,
    get: () => "radio",
  },
  value: {
    configurable: true,
    get: () => selectedValue.value,
    set: (value) => {
      selectedValue.value = selectionOrNull(value);
    },
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

// Disabled is governed by the form-control composable: presence-based own attribute plus ancestor
// <fieldset disabled>, so the group matches :disabled the way a native control in one does.
const { disabled: isDisabled } = useFormControl({ disabled: toRef(props, "disabled") });
const isReadonly = computed(() => toBoolean(props.readonly));
const hasError = computed(() => toBoolean(props.error) || hasErrorMessages.value);

const hasErrorMessages = computed(() => errorMessageList.value.length > 0);
const errorMessageList = computed(() => toArray(props.errorMessages ?? []).filter(Boolean));
const errorText = computed(() => errorMessageList.value.join(" "));
const showError = computed(() => hasError.value && Boolean(errorText.value));

// Instance-unique id so two <sp-radio-group> on one page don't collide on a
// hard-coded label id (which would break aria-labelledby for the second group).
// A random suffix avoids cross-instance collisions without module-level state;
// Vue's useId() can't be used here (each custom element mounts as its own app,
// so every instance would get the same "v-0").
const labelId = `sp-radio-group-label-${Math.random().toString(36).slice(2, 10)}`;
const ariaLabelledby = computed(() => (props.label ? labelId : undefined));
const ariaInvalid = computed(() => (hasError.value ? "true" : undefined));
const ariaDisabled = computed(() => (isDisabled.value ? "true" : undefined));

const classModifiers = computed(() => ({
  "--inline": toBoolean(props.inline),
  "--disabled": isDisabled.value,
  "--error": hasError.value,
}));

const radios = computed(() => getSlotContentByTagName(itemsContainer, "SP-RADIO"));

// Slotted radios upgrade after the group's first sync; un-upgraded elements have no prop
// accessors yet, so fall back to the attribute. A radio with neither reads `undefined`, which
// never matches the group's `null` "nothing selected".
function radioValue(radio) {
  return radio.value ?? radio.getAttribute("value") ?? undefined;
}

// Radios may already be present synchronously or arrive asynchronously via
// slot population, and the set can change over the component's life. Re-run on
// every change (immediately) and only bind listeners on radios we haven't seen.
const listenedRadios = new WeakSet();
// A radio's own disabled state, captured before the group ever overrides it, so
// re-enabling the group restores each radio's intrinsic disabled rather than
// clearing it.
const ownDisabled = new WeakMap();
watch(radios, setupRadios, { immediate: true });

// Re-reflect the checked state and roving tabindex whenever the selection moves — synchronously,
// so a `change` listener already sees the picked radio checked.
watch(selectedValue, syncRadios, { flush: "sync" });

// Toggling `disabled` after mount changes both the visual disabled state of each
// radio and which radios are tab-eligible (a disabled group keeps no radio
// tabbable), so both must recompute.
watch(isDisabled, syncDisabled);

function setupRadios() {
  radios.value.forEach((radio) => {
    if (listenedRadios.has(radio)) {
      return;
    }
    listenedRadios.add(radio);
    // Capture from the attribute (reliable before the child upgrades, and read
    // before syncDisabled overwrites it) so the radio's own disabled survives the
    // group toggling its disabled state.
    ownDisabled.set(radio, isPresent(radio.getAttribute("disabled")));
    useEventListener(radio, "click", () => activateRadio(radio));
    useEventListener(radio, "keydown", (event) => handleKeydown(event, radio));
  });
  syncRadios();
}

function syncRadios() {
  radios.value.forEach((radio) => {
    radio.setAttribute("checked", String(radioValue(radio) === selectedValue.value));
  });
  syncDisabled();
}

// Reflect the group's disabled state onto each child so they look disabled
// (opacity/cursor), keeping any radio that is intrinsically disabled disabled.
function syncDisabled() {
  radios.value.forEach((radio) => {
    const intrinsic = ownDisabled.get(radio) ?? isPresent(radio.getAttribute("disabled"));
    radio.setAttribute("disabled", String(isDisabled.value || intrinsic));
  });
  updateRovingTabindex();
}

// Exactly one radio carries tabindex=0: the selected one, or — when nothing is
// selected yet — the first enabled radio, so the group is reachable by keyboard.
function updateRovingTabindex() {
  const enabled = radios.value.filter((radio) => !isRadioDisabled(radio));
  const tabbableRadio = enabled.find((radio) => radioValue(radio) === selectedValue.value) ?? enabled.at(0);

  radios.value.forEach((radio) => {
    radio.setAttribute("tabbable", String(radio === tabbableRadio));
  });
}

function activateRadio(radio) {
  if (interactionBlocked() || isRadioDisabled(radio)) {
    return;
  }
  select(radioValue(radio));
}

function handleKeydown(event, radio) {
  const direction = NAVIGATION_KEYS[event.key];

  if (direction) {
    event.preventDefault();
    if (interactionBlocked()) {
      return;
    }
    const next = nextEnabledRadio(radio, direction);
    if (next) {
      select(radioValue(next));
      next.focusRadio();
    }
  } else if (event.key === " " || event.key === "Spacebar") {
    event.preventDefault();
    activateRadio(radio);
  }
}

function nextEnabledRadio(current, direction) {
  const enabled = radios.value.filter((radio) => !isRadioDisabled(radio));
  if (!enabled.length) {
    return undefined;
  }

  const currentIndex = enabled.indexOf(current);
  // When the focused radio is itself disabled it won't be in `enabled`; start
  // from the head so navigation still moves to a usable radio.
  const startIndex = currentIndex === -1 ? 0 : currentIndex;
  const nextIndex = (startIndex + direction + enabled.length) % enabled.length;

  return enabled[nextIndex];
}

// The only path that reports `input` and `change`: property writes, attribute syncs, and resets
// mirror the form value silently.
function select(value) {
  const selection = selectionOrNull(value);

  if (selectedValue.value === selection) {
    return;
  }
  selectedValue.value = selection;
  emitBubbling("input", selection);
  emitChange();
}

function interactionBlocked() {
  return isDisabled.value || isReadonly.value;
}

// The group propagates its own disabled to each child's `disabled` attribute, so
// a radio's intrinsic disabled is the captured value (the live attribute now
// also carries the group state). Group disabled is folded in via isDisabled.
function isRadioDisabled(radio) {
  return isDisabled.value || (ownDisabled.get(radio) ?? toBoolean(radio.disabled));
}

const NAVIGATION_KEYS = {
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
};

// Form association: expose the selected value as the element's form value so a
// native <form> submit (and the doctor-finder bridge) picks it up under `name`.
// A null selection clears the entry, the way an unchecked radio set submits nothing.
const { emitChange, mirrorFormValue, mirrorValueMissing, onFormReset } = useForm();

mirrorFormValue(selectedValue);

onFormReset(() => {
  selectedValue.value = defaultValue.value;
});

// A required group is satisfied only once one of its radios is chosen.
const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("radio_group_value_missing"));

mirrorValueMissing(() => isRequired.value && selectedValue.value == null, validationMessage, itemsContainer);
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-radio-group {
  display: flex;
  flex-direction: column;
  gap: var(--sp-comp-radio-group-gap, 0.75rem);
}

.sp-radio-group__label {
  margin-bottom: var(--sp-comp-radio-group-label-margin-bottom, var(--sp-ref-scale-4, 0.25rem));
  --sp-comp-text-label-short: var(--sp-comp-radio-group-label-font, var(--sp-sys-form-field-label-short));
}

.sp-radio-group__items {
  display: flex;
  flex-direction: column;
  gap: var(--sp-comp-radio-group-items-gap, 0.5rem);
}

.sp-radio-group.--inline .sp-radio-group__items {
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--sp-comp-radio-group-items-inline-gap, 1.5rem);
}

.sp-radio-group__error {
  color: var(--sp-comp-radio-group-error-color, #d32f2f);
  font-size: var(--sp-comp-radio-group-error-font-size, 0.8125rem);
}
</style>
